import { Video } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { UpcomingInterviewEntry } from '@hygge/shared';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useUpcomingInterviews } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';
import { Chip } from '../chip';
import { candidateInitials } from '../widget-format';

/**
 * Compose the chip label from the BE's company-timezone parts: "Today 3:00 PM" ·
 * "Tomorrow 11:00 AM" · "Jul 24, 2:00 PM". The relative day and the time are resolved
 * server-side in the company timezone, so the widget never re-derives "today" from the
 * viewer's browser zone (which would disagree with the other dashboard "today" widgets).
 */
function whenLabel(i: UpcomingInterviewEntry): string {
  if (i.relativeDay === 'today') return `Today ${i.timeLabel}`;
  if (i.relativeDay === 'tomorrow') return `Tomorrow ${i.timeLabel}`;
  return `${i.dateLabel}, ${i.timeLabel}`;
}

function InterviewRow({ i }: { i: UpcomingInterviewEntry }) {
  return (
    <Link
      to="/recruitment/candidates/$id/interview"
      params={{ id: i.candidateId }}
      className="dashboard-row group flex items-center gap-3 border-b border-border py-2.5 last:border-0"
    >
      <Avatar className="h-8 w-8">
        <AvatarFallback className="text-xs">{candidateInitials(i.candidateName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium group-hover:underline">{i.candidateName}</div>
        {i.position && <div className="truncate text-xs text-muted-foreground">{i.position}</div>}
      </div>
      <Chip color={i.relativeDay === 'today' ? 'blue' : 'gray'}>{whenLabel(i)}</Chip>
    </Link>
  );
}

/**
 * `upcomingInterviews` widget — the caller's future scheduled interviews
 * (recruiter → own candidates; lead/super_admin → all), soonest first. Each row links
 * to the candidate's Interview tab.
 */
export function UpcomingInterviewsWidget() {
  const { data, isLoading, isError } = useUpcomingInterviews();
  const rows = data ?? [];

  return (
    <WidgetCard title="Upcoming Interviews" icon={Video}>
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3">
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load upcoming interviews.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">No interviews scheduled.</p>
      ) : (
        <ExpandableList
          items={rows}
          getKey={(i) => i.id}
          renderItem={(i: UpcomingInterviewEntry) => <InterviewRow i={i} />}
        />
      )}
    </WidgetCard>
  );
}
